// modals/bank/AddBankAccount.jsx
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Save,
    X,
    User,
    //Building, 
    Hash,
    CreditCard,
    MapPin,
    Phone,
    Mail,
    DollarSign,
    FileText,
    Home,

} from 'react-feather';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { bankActions } from '../../action/bankActions';

const AddBankAccount = ({ onSuccess, onClose, isOpen = false }) => {
    const [formData, setFormData] = useState({
        account_name: '',
        bank_name: '',
        account_number: '',
        account_type: 'savings',
        ifsc_code: '',
        branch_name: '',
        opening_balance: 0,
        current_balance: 0,
        currency: 'INR',
        contact_person: '',
        contact_phone: '',
        contact_email: '',
        address: '',
        description: '',
        is_default: false,
        status: 'active'
    });

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    // Account type options
    const accountTypeOptions = [
        { value: 'savings', label: 'Savings Account' },
        { value: 'current', label: 'Current Account' },
        { value: 'cash', label: 'Cash Account' },
        { value: 'credit_card', label: 'Credit Card' },
        { value: 'loan', label: 'Loan Account' }
    ];

    // Currency options
    const currencyOptions = [
        { value: 'INR', label: '₹ Indian Rupee' },
        { value: 'USD', label: '$ US Dollar' },
        { value: 'EUR', label: '€ Euro' },
        { value: 'GBP', label: '£ British Pound' }
    ];

    // Clean up modal backdrops
    const cleanupModalBackdrops = () => {
        const backdrops = document.querySelectorAll('.modal-backdrop');
        backdrops.forEach(backdrop => backdrop.remove());
        document.body.classList.remove('modal-open');
        document.body.style.paddingRight = '';
        document.body.style.overflow = '';
    };

    // Show Bootstrap modal
    const showBootstrapModal = () => {
        cleanupModalBackdrops();
        const modalElement = document.getElementById('add-bank-account');
        if (modalElement) {
            if (typeof window.$ !== 'undefined' && window.$.fn.modal) {
                window.$(modalElement).modal('show');
            } else if (typeof window.bootstrap !== 'undefined' && window.bootstrap.Modal) {
                let modalInstance = window.bootstrap.Modal.getInstance(modalElement);
                if (!modalInstance) {
                    modalInstance = new window.bootstrap.Modal(modalElement);
                }
                modalInstance.show();
            } else {
                modalElement.classList.add('show');
                modalElement.style.display = 'block';
                const backdrop = document.createElement('div');
                backdrop.className = 'modal-backdrop fade show';
                document.body.appendChild(backdrop);
                document.body.classList.add('modal-open');
            }
        }
    };

    // Hide Bootstrap modal
    const hideBootstrapModal = () => {
        const modalElement = document.getElementById('add-bank-account');
        if (modalElement) {
            if (typeof window.$ !== 'undefined' && window.$.fn.modal) {
                window.$(modalElement).modal('hide');
                setTimeout(cleanupModalBackdrops, 300);
            } else if (typeof window.bootstrap !== 'undefined' && window.bootstrap.Modal) {
                const modalInstance = window.bootstrap.Modal.getInstance(modalElement);
                if (modalInstance) {
                    modalInstance.hide();
                    setTimeout(cleanupModalBackdrops, 300);
                }
            } else {
                modalElement.classList.remove('show');
                modalElement.style.display = 'none';
                cleanupModalBackdrops();
            }
        }
    };

    useEffect(() => {
        if (isOpen) {
            setTimeout(() => showBootstrapModal(), 50);
        }
        return () => {
            cleanupModalBackdrops();
        };
    }, [isOpen]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleSelectChange = (name, option) => {
        setFormData(prev => ({
            ...prev,
            [name]: option?.value || ''
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.account_name.trim()) {
            newErrors.account_name = 'Account holder name is required';
        }

        if (!formData.bank_name.trim()) {
            newErrors.bank_name = 'Bank name is required';
        }

        if (!formData.account_number.trim()) {
            newErrors.account_number = 'Account number is required';
        } else if (!/^\d{9,18}$/.test(formData.account_number.replace(/\s/g, ''))) {
            newErrors.account_number = 'Enter a valid account number (9-18 digits)';
        }

        if (formData.ifsc_code && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifsc_code)) {
            newErrors.ifsc_code = 'Enter a valid IFSC code (e.g., SBIN0123456)';
        }

        if (formData.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
            newErrors.contact_email = 'Enter a valid email address';
        }

        if (formData.contact_phone && !/^[0-9+\-\s]{10,15}$/.test(formData.contact_phone)) {
            newErrors.contact_phone = 'Enter a valid phone number';
        }

        if (formData.opening_balance < 0) {
            newErrors.opening_balance = 'Opening balance cannot be negative';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            Swal.fire('Error', 'Please fix the errors in the form', 'error');
            return;
        }

        try {
            setLoading(true);

            // Set current balance same as opening balance for new account
            const dataToSend = {
                ...formData,
                current_balance: formData.opening_balance
            };

            const result = await bankActions.createAccount(dataToSend);

            if (result.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Bank account created successfully',
                    timer: 2000,
                    showConfirmButton: false
                });

                hideBootstrapModal();
                if (onSuccess) onSuccess();
            } else {
                Swal.fire('Error', result.message || 'Failed to create bank account', 'error');
            }
        } catch (error) {
            console.error('Error creating bank account:', error);
            Swal.fire('Error', error?.message || 'Failed to create bank account', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        if (Object.values(formData).some(val => val && val !== '' && val !== 0 && val !== false)) {
            Swal.fire({
                title: 'Discard Changes?',
                text: 'You have unsaved changes. Are you sure?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Yes, discard'
            }).then((result) => {
                if (result.isConfirmed) {
                    hideBootstrapModal();
                    if (onClose) onClose();
                }
            });
        } else {
            hideBootstrapModal();
            if (onClose) onClose();
        }
    };

    return (
        <div className="modal fade" id="add-bank-account" tabIndex="-1" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Add New Bank Account</h5>
                        <button type="button" className="btn-close" onClick={handleCancel}></button>
                    </div>

                    <div className="modal-body">
                        <form onSubmit={handleSubmit}>
                            {/* Account Information */}
                            <h6 className="fw-bold mb-3">Account Information</h6>
                            <div className="row g-3 mb-3">
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">
                                        <User size={14} className="me-1" />
                                        Account Holder Name <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.account_name ? 'is-invalid' : ''}`}
                                        name="account_name"
                                        value={formData.account_name}
                                        onChange={handleChange}
                                        placeholder="Enter account holder name"
                                    />
                                    {errors.account_name && (
                                        <div className="invalid-feedback">{errors.account_name}</div>
                                    )}
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label fw-bold">
                                        <Home size={14} className="me-1" />
                                        Bank Name <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.bank_name ? 'is-invalid' : ''}`}
                                        name="bank_name"
                                        value={formData.bank_name}
                                        onChange={handleChange}
                                        placeholder="Enter bank name"
                                    />
                                    {errors.bank_name && (
                                        <div className="invalid-feedback">{errors.bank_name}</div>
                                    )}
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label fw-bold">
                                        <Hash size={14} className="me-1" />
                                        Account Number <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.account_number ? 'is-invalid' : ''}`}
                                        name="account_number"
                                        value={formData.account_number}
                                        onChange={handleChange}
                                        placeholder="Enter account number"
                                    />
                                    {errors.account_number && (
                                        <div className="invalid-feedback">{errors.account_number}</div>
                                    )}
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label fw-bold">
                                        <CreditCard size={14} className="me-1" />
                                        Account Type
                                    </label>
                                    <Select
                                        options={accountTypeOptions}
                                        value={accountTypeOptions.find(opt => opt.value === formData.account_type)}
                                        onChange={(opt) => handleSelectChange('account_type', opt)}
                                        placeholder="Select type"
                                        isClearable={false}
                                    />
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label fw-bold">IFSC Code</label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.ifsc_code ? 'is-invalid' : ''}`}
                                        name="ifsc_code"
                                        value={formData.ifsc_code}
                                        onChange={handleChange}
                                        placeholder="e.g., SBIN0123456"
                                        maxLength="11"
                                    />
                                    {errors.ifsc_code && (
                                        <div className="invalid-feedback">{errors.ifsc_code}</div>
                                    )}
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label fw-bold">Branch Name</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="branch_name"
                                        value={formData.branch_name}
                                        onChange={handleChange}
                                        placeholder="Enter branch name"
                                    />
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label fw-bold">Currency</label>
                                    <Select
                                        options={currencyOptions}
                                        value={currencyOptions.find(opt => opt.value === formData.currency)}
                                        onChange={(opt) => handleSelectChange('currency', opt)}
                                        placeholder="Select currency"
                                        isClearable={false}
                                    />
                                </div>
                            </div>

                            {/* Balance Information */}
                            <h6 className="fw-bold mb-3 mt-4">Balance Information</h6>
                            <div className="row g-3 mb-3">
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">
                                        <DollarSign size={14} className="me-1" />
                                        Opening Balance
                                    </label>
                                    <div className="input-group">
                                        <span className="input-group-text">₹</span>
                                        <input
                                            type="number"
                                            className={`form-control ${errors.opening_balance ? 'is-invalid' : ''}`}
                                            name="opening_balance"
                                            value={formData.opening_balance}
                                            onChange={handleChange}
                                            min="0"
                                            placeholder="0.00"
                                        />
                                    </div>
                                    {errors.opening_balance && (
                                        <div className="text-danger small mt-1">{errors.opening_balance}</div>
                                    )}
                                    <small className="text-muted">Current balance will be same as opening</small>
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label fw-bold">
                                        <FileText size={14} className="me-1" />
                                        Description
                                    </label>
                                    <textarea
                                        className="form-control"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        rows="2"
                                        placeholder="Additional notes about this account"
                                    />
                                </div>
                            </div>

                            {/* Contact Information */}
                            <h6 className="fw-bold mb-3 mt-4">Contact Information</h6>
                            <div className="row g-3 mb-3">
                                <div className="col-md-4">
                                    <label className="form-label fw-bold">Contact Person</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="contact_person"
                                        value={formData.contact_person}
                                        onChange={handleChange}
                                        placeholder="Contact person name"
                                    />
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label fw-bold">
                                        <Phone size={14} className="me-1" />
                                        Phone
                                    </label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.contact_phone ? 'is-invalid' : ''}`}
                                        name="contact_phone"
                                        value={formData.contact_phone}
                                        onChange={handleChange}
                                        placeholder="Phone number"
                                    />
                                    {errors.contact_phone && (
                                        <div className="invalid-feedback">{errors.contact_phone}</div>
                                    )}
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label fw-bold">
                                        <Mail size={14} className="me-1" />
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        className={`form-control ${errors.contact_email ? 'is-invalid' : ''}`}
                                        name="contact_email"
                                        value={formData.contact_email}
                                        onChange={handleChange}
                                        placeholder="Email address"
                                    />
                                    {errors.contact_email && (
                                        <div className="invalid-feedback">{errors.contact_email}</div>
                                    )}
                                </div>

                                <div className="col-12">
                                    <label className="form-label fw-bold">
                                        <MapPin size={14} className="me-1" />
                                        Address
                                    </label>
                                    <textarea
                                        className="form-control"
                                        name="address"
                                        value={formData.address}
                                        onChange={handleChange}
                                        rows="2"
                                        placeholder="Enter address"
                                    />
                                </div>
                            </div>

                            {/* Additional Options */}
                            <div className="row g-3 mb-3">
                                <div className="col-12">
                                    <div className="form-check">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id="is_default"
                                            name="is_default"
                                            checked={formData.is_default}
                                            onChange={handleChange}
                                        />
                                        <label className="form-check-label" htmlFor="is_default">
                                            Set as default account for transactions
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="text-end mt-4">
                                <button
                                    type="button"
                                    className="btn btn-secondary me-2"
                                    onClick={handleCancel}
                                >
                                    <X size={14} className="me-1" />
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" />
                                            Creating...
                                        </>
                                    ) : (
                                        <>
                                            <Save size={14} className="me-1" />
                                            Create Account
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

AddBankAccount.propTypes = {
    onSuccess: PropTypes.func,
    onClose: PropTypes.func.isRequired,
    isOpen: PropTypes.bool
};

export default AddBankAccount;